/*
 * Privado mobile-first motion layer.
 *
 * The base theme disables its scroll/parallax effects on touch + small
 * screens (every motion custom element returns mobileDisabled = true), so
 * phones get a dead static fallback. This layer is the opposite: it ONLY
 * runs <= 749px and turns scrolling into the arcade "adventure" the desktop
 * has — scroll-driven focus, parallax drift, neon glass that lights up in
 * the same gradient the journey line uses.
 *
 * It is a small scene system: any element tagged [data-pv-scene="<name>"] is
 * picked up and enhanced. Add scenes here as we expand across the homepage.
 */
(() => {
  const SMALL = window.matchMedia('(max-width: 749px)');
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

  // Walk up the DOM until we hit a non-transparent background, then decide
  // whether the scene sits on a light or dark surface so the glass adapts.
  const sceneIsDark = (el) => {
    let node = el;
    while (node && node !== document.documentElement) {
      const bg = getComputedStyle(node).backgroundColor;
      const m = bg.match(/rgba?\(([^)]+)\)/);
      if (m) {
        const parts = m[1].split(',').map((n) => parseFloat(n));
        const alpha = parts[3] === undefined ? 1 : parts[3];
        if (alpha > 0.1) {
          const [r, g, b] = parts;
          const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
          return lum < 0.5;
        }
      }
      node = node.parentElement;
    }
    return false;
  };

  const scenes = [];

  const viewportFocus = (element, power = 1.45) => {
    const rect = element.getBoundingClientRect();
    const mid = window.innerHeight / 2;
    const elementMid = rect.top + rect.height / 2;
    return clamp(1 - (Math.abs(elementMid - mid) / window.innerHeight) * power, 0, 1);
  };

  const buildHero = (scene) => {
    const media = scene.querySelector('.banner__image');
    const words = scene.querySelector('.slideshow-content');
    if (!media || !words) return null;

    /* PERF: the hero is now STATIC on mobile.
       The old scroll-driven parallax pushed translate + SCALE onto the big hero
       image (and copy / liquid / rail) on every single scroll frame. That forced
       the sticky header's backdrop-filter to re-blur continuously and made both
       scrolling AND opening the menu jerk ("itch and jerk"). Rescaling a full-bleed
       image every frame is one of the most expensive things you can do on a phone.
       We drop all of it — no scroll/resize listeners, no per-frame writes. The
       image just rests at its default scale; the slideshow crossfade + the gentle
       arcade-ball loop carry the motion (both cheap, composite-only). */
    scene.classList.add('pv-scene--active', 'pv-hero-scene');

    return () => {
      scene.classList.remove('pv-scene--active', 'pv-hero-scene');
    };
  };

  const buildCategory = (scene) => {
    const cards = Array.from(scene.querySelectorAll('.category-gateway__feature'));
    const buttons = Array.from(scene.querySelectorAll('.category-gateway__links .button'));
    if (!cards.length && !buttons.length) return null;

    scene.classList.add('pv-scene--active', 'pv-category-scene');
    [...cards, ...buttons].forEach((item, i) => {
      item.style.setProperty('--pv-i', i);
      item.classList.add('pv-mobile-item');
    });

    let raf = 0;
    const update = () => {
      raf = 0;

      cards.forEach((card) => {
        const rect = card.getBoundingClientRect();
        const center = rect.left + rect.width / 2;
        const focus = clamp(1 - Math.abs(center - window.innerWidth / 2) / Math.max(window.innerWidth * 0.7, 1), 0, 1);
        card.style.setProperty('--pv-focus', focus.toFixed(4));
        card.style.setProperty('--pv-lift', `${(-10 * focus).toFixed(1)}px`);
      });

      buttons.forEach((button, i) => {
        const focus = viewportFocus(button, 1.65);
        button.style.setProperty('--pv-focus', focus.toFixed(4));
        button.style.setProperty('--pv-shift', `${((focus * 12) - (i % 2) * 4).toFixed(1)}px`);
      });
    };

    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    const onHorizontal = () => onScroll();

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    scene.querySelector('.category-gateway__features')?.addEventListener('scroll', onHorizontal, { passive: true });
    update();

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      scene.querySelector('.category-gateway__features')?.removeEventListener('scroll', onHorizontal);
      if (raf) cancelAnimationFrame(raf);
      scene.classList.remove('pv-scene--active', 'pv-category-scene');
      scene.style.removeProperty('--pv-glow');
      [...cards, ...buttons].forEach((item) => {
        item.classList.remove('pv-mobile-item');
        ['--pv-i', '--pv-focus', '--pv-lift', '--pv-shift'].forEach((p) => item.style.removeProperty(p));
      });
    };
  };

  const buildOrbit = (scene) => {
    const field = scene.querySelector('.collaboration-orbit__field');
    const planets = Array.from(scene.querySelectorAll('.collaboration-orbit__planet-inner'));
    if (!field) return null;

    const ORBIT_SPIN = 260; // deg of rotation swung across the full scroll-through

    scene.classList.add('pv-scene--active', 'pv-orbit-scene');
    planets.forEach((planet, i) => planet.style.setProperty('--pv-i', i));

    let raf = 0;
    const update = () => {
      raf = 0;
      const rect = scene.getBoundingClientRect();
      const progress = clamp((window.innerHeight - rect.top) / (window.innerHeight + rect.height), 0, 1);
      const focus = viewportFocus(scene, 1.2);
      scene.style.setProperty('--pv-progress', progress.toFixed(4));
      scene.style.setProperty('--pv-focus', focus.toFixed(4));
      field.style.setProperty('--pv-orbit-y', `${((progress - 0.5) * 26).toFixed(1)}px`);
      field.style.setProperty('--pv-orbit-scale', (0.965 + focus * 0.05).toFixed(4));

      // Scroll-driven spin (mirrors desktop's --orbit-scroll-N engine, which is
      // dead on mobile): rings rotate with scroll and rest when you stop.
      // easeInOutQuad so it eases as the section enters/leaves view.
      const eased = progress < 0.5 ? 2 * progress * progress : 1 - Math.pow(-2 * progress + 2, 2) / 2;
      const deg = (eased - 0.5) * ORBIT_SPIN;
      [deg, deg * -0.72, deg * 1.18].forEach((r, i) => {
        scene.style.setProperty(`--orbit-scroll-${i + 1}`, `${r.toFixed(2)}deg`);
        scene.style.setProperty(`--orbit-scroll-${i + 1}-inverse`, `${(-r).toFixed(2)}deg`);
      });
    };

    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();

    // Reveal: the Privado core zooms out (cubic ease), then the logos fade in,
    // once the section scrolls into view.
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            scene.classList.add('pv-orbit-in');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.25 }
    );
    io.observe(scene);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      io.disconnect();
      if (raf) cancelAnimationFrame(raf);
      scene.classList.remove('pv-scene--active', 'pv-orbit-scene', 'pv-orbit-in');
      ['--pv-progress', '--pv-focus', '--pv-glow',
        '--orbit-scroll-1', '--orbit-scroll-1-inverse',
        '--orbit-scroll-2', '--orbit-scroll-2-inverse',
        '--orbit-scroll-3', '--orbit-scroll-3-inverse'].forEach((p) => scene.style.removeProperty(p));
      ['--pv-orbit-y', '--pv-orbit-scale'].forEach((p) => field.style.removeProperty(p));
      planets.forEach((planet) => planet.style.removeProperty('--pv-i'));
    };
  };

  const buildReviews = (scene) => {
    const cards = Array.from(scene.querySelectorAll('.testimonial'));
    if (!cards.length) return null;

    scene.classList.add('pv-scene--active');
    scene.classList.toggle('pv-scene--on-dark', sceneIsDark(scene));

    cards.forEach((card, i) => {
      card.classList.add('pv-card');
      card.style.setProperty('--pv-i', i);
    });

    // Privado HOME-7 (scope.md): reviews are STATIC. The scroll-scrubbed
    // zoom/drift + text parallax was removed by request — the heading stays in
    // place at the top and the cards don't slide under it. The cards are
    // liquid-glass tiles styled by the section's own CSS; here we only tag them
    // visible (identity transform) so nothing is gated on a scroll listener.
    cards.forEach((card) => {
      card.style.setProperty('--rev-scale', '1');
      card.style.setProperty('--rev-y', '0px');
      card.classList.add('pv-in');
    });

    return () => {
      scene.classList.remove('pv-scene--active', 'pv-scene--on-dark');
      cards.forEach((card) => {
        card.classList.remove('pv-card', 'pv-in');
        ['--pv-i', '--rev-scale', '--rev-y'].forEach((p) =>
          card.style.removeProperty(p)
        );
      });
    };
  };

  const BUILDERS = {
    hero: buildHero,
    category: buildCategory,
    orbit: buildOrbit,
    reviews: buildReviews
  };

  // Soft site-wide reveal: section headings + rich text fade/rise in as they
  // enter view. Opt-in per element via a class the JS adds, plus a safety timer
  // that reveals everything after 1.6s so content can NEVER stay hidden if the
  // observer misfires. Transform/opacity only; skips the bespoke pv scenes.
  const buildReveal = () => {
    const main = document.getElementById('MainContent');
    if (!main) return null;

    const targets = [];
    main.querySelectorAll('.title-wrapper, .rte').forEach((el) => {
      if (el.closest('[data-pv-scene]') || el.dataset.pvReveal) return;
      el.dataset.pvReveal = '1';
      el.classList.add('pv-reveal');
      targets.push(el);
    });
    if (!targets.length) return null;

    const reveal = (el) => el.classList.add('pv-revealed');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            reveal(entry.target);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -5% 0px' }
    );
    targets.forEach((el) => io.observe(el));

    const safety = window.setTimeout(() => targets.forEach(reveal), 1600);

    return () => {
      io.disconnect();
      window.clearTimeout(safety);
      targets.forEach((el) => {
        el.classList.remove('pv-reveal', 'pv-revealed');
        delete el.dataset.pvReveal;
      });
    };
  };

  let active = false;

  const enable = () => {
    if (active) return;
    active = true;
    document.querySelectorAll('[data-pv-scene]').forEach((scene) => {
      const builder = BUILDERS[scene.getAttribute('data-pv-scene')];
      if (!builder) return;
      // Isolate each scene: a throw in one must never abort the others.
      try {
        const teardown = builder(scene);
        if (teardown) scenes.push(teardown);
      } catch (err) {
        console.warn('[pv-motion] scene failed:', scene.getAttribute('data-pv-scene'), err);
      }
    });
    try {
      const revealTeardown = buildReveal();
      if (revealTeardown) scenes.push(revealTeardown);
    } catch (err) {
      console.warn('[pv-motion] reveal failed:', err);
    }
  };

  const disable = () => {
    active = false;
    while (scenes.length) scenes.pop()();
  };

  const evaluate = () => {
    if (SMALL.matches) enable();
    else disable();
  };

  const init = () => {
    evaluate();
    SMALL.addEventListener('change', evaluate);
    REDUCED.addEventListener('change', evaluate);
    // Re-run when sections are edited in the theme customizer.
    document.addEventListener('shopify:section:load', () => {
      disable();
      evaluate();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
